// -----------------------------------------------------------------------------
// APP ROOT — session gate + route switch.
//   1. Ask the backend who is signed in (me()). Nobody → LoginPage. (v0.8.0: #/forgot-password and #/set-password
//      come first — they work while nobody is signed in.)
//   2. Put { me, signOut } in SessionContext for every page.
//   3. Map the current Route (core/router.ts) to a page inside the Shell.
// Adding a module page: add its route in router.ts, then one line in renderRoute().
// -----------------------------------------------------------------------------
import { PrintHost } from "../modules/issues/print/printer";
import { useCallback, useEffect, useState } from "react";
import { getBackend, rpc } from "../core/api/backend";
import type { Me } from "../core/api/types";
import { pageKey, projectCodeOf, replaceHash, useRoute, type Route } from "../core/router";
import { SessionContext } from "../core/session";
import { Shell } from "../core/layout/Shell";
import { GuardHost, ToastHost, toast } from "../core/ui/kit";
import { setAppToday } from "../core/format";
import { Loading } from "../core/ui/kit";
import { LoginPage } from "../core/pages/LoginPage";
import { ForgotPasswordPage, SetPasswordPage } from "../core/pages/AuthPages";
import { afterSignIn, authScreen, projectOfPath, signInHash } from "../core/authLinks";
import { ProjectsPage } from "../core/pages/ProjectsPage";
import { DashboardPage } from "../core/pages/DashboardPage";
import { ActivityPage } from "../core/pages/ActivityPage";
import { NotFoundPage, SettingsPage } from "../core/pages/SimplePages";
import { AdminPage } from "../core/pages/AdminPage";
import { NotificationsPage } from "../core/pages/NotificationsPage";
import { OverviewPage } from "../modules/issues/pages/OverviewPage";
import { ComponentPage } from "../modules/issues/pages/ComponentPage";
import { IssuePage } from "../modules/issues/pages/IssuePage";
import { SetupPage } from "../modules/issues/pages/SetupPage";

export function renderRoute(r: Route) {
  switch (r.name) {
    case "projects": return <ProjectsPage />;
    case "dashboard": return <DashboardPage code={r.code} />;
    case "activity": return <ActivityPage code={r.code} />;
    case "issues": return <OverviewPage code={r.code} history={r.history} />;
    case "component": return <ComponentPage code={r.code} prefix={r.prefix} />;
    case "issue": return <IssuePage code={r.code} prefix={r.prefix} issue={r.issue} />;
    case "setup": return <SetupPage code={r.code} />;
    case "admin": return <AdminPage route={r} />;
    case "settings": return <SettingsPage />;
    case "notifications": return <NotificationsPage tab={r.tab ?? "action"} />;
    default: return <NotFoundPage />;
  }
}

export function App() {
  const route = useRoute();
  const [me, setMe] = useState<Me | null | undefined>(undefined);   // undefined = still checking
  const [refused, setRefused] = useState<string | null>(null);      // e.g. "This account has been deactivated…" (Part 3)

  const loadMe = useCallback(async () => {
    const b = await getBackend();
    if (!(await b.currentUserId())) return setMe(null);
    try {
      const m = await rpc("me"); setAppToday(m.today);
      // v0.9.2 #3: signed in from #/sign-in?next=… → on to the linked page; a project you can't open → Projects + a message
      const go = afterSignIn(window.location.hash);
      if (go) {
        const code = projectOfPath(go);
        let ok = true;
        if (code) { try { await rpc("project_context", { code }); } catch { ok = false; } }
        replaceHash(ok ? go : "#/");                     // replaces #/sign-in: Back never returns to the sign-in address
        if (!ok) toast(`You can't open ${code} — ask the super admin to add you to it`, 6000);
      }
      setMe(m); setRefused(null);
    }
    catch (e) { setRefused((e as Error).message); await b.signOut(); setMe(null); }
  }, []);
  // v0.9.1 #8: not while an emailed link is being opened — checking an older sign-in at the same moment could wipe the new one
  // v0.9.2 #3: signed out → the address shows the sign-in page, keeping the linked page in ?next=
  useEffect(() => {
    if (me !== null) return;
    const to = signInHash(window.location.hash);
    if (to) replaceHash(to);                          // through the router, so its history count stays right
  }, [me]);
  const onLink = authScreen(window.location.hash)?.name === "set-password";
  useEffect(() => { if (!onLink) loadMe(); }, [loadMe, onLink]);   // leaving the link page (Back to sign in) checks again

  const signOut = useCallback(async () => {
    await (await getBackend()).signOut();
    window.location.hash = "#/";
    setMe(null);
  }, []);

  // v0.8.0: A0.1 Forgot password / A0.2 Set password work whether or not someone is signed in (core/authLinks.ts)
  const screen = authScreen(window.location.hash);
  if (screen?.name === "forgot") return <ForgotPasswordPage />;
  // the link signs its own person in: forget whoever was signed in on this browser before (v0.8.1)
  if (screen?.name === "set-password") return <SetPasswordPage tokenHash={screen.tokenHash} type={screen.type} onDone={() => { setMe(undefined); loadMe(); }} />;
  if (me === undefined) return <Loading />;
  if (me === null) return <LoginPage onSignedIn={loadMe} notice={refused} />;
  return (
    <SessionContext.Provider value={{ me, signOut }}>
      <Shell route={route} projectCode={projectCodeOf(route)}>
        {/* key: remount the page on navigation so state never leaks between pages — but not when only a pop-up in the
            address opens or closes (A3 ↔ A3.2 Publish history: v0.7.1, no reload flash) */}
        <div key={pageKey(route, window.location.hash)}>{renderRoute(route)}</div>
      </Shell>
      <ToastHost />
      <GuardHost />
      <PrintHost />
    </SessionContext.Provider>
  );
}
